import { OpenGovAppConfig } from '@opengov/app-config';
import { Box, Button, Chip, Collapse, IconButton, Tab, Tabs, TextField, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PATH_EDIT, STATE_DRAFT, STATE_INTERNAL_PUBLISHED, STATE_PUBLISHED } from '@opengov/stories-shared/constants';
import {
  IBudget,
  IAllProposalDetails,
  INewStoryContent,
  IPublication,
  IStory,
  IStoryContent,
  IStoryMeta,
  IStoryState
} from '@opengov/stories-shared/types';
import { orderStoryContents, sortStoryContents } from '@opengov/stories-shared/utils/general';
import moment from 'moment';
import React, { useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { IEditorMessages, IModalMessages, IStoryViewMessages, IUpdateStoryContentProps } from '../../../../types';
import { getNewestUpdatedAt } from '../../../utils/helper';
import { getStoryStatusChipParams } from '../../../common/utils';

import { StoryContents } from '../StoryContents';
import { ProposalsView } from '../Proposals/ProposalsView/ProposalsView';

import { styles } from './Sidebar.styles';

export type ISidebarProps = {
  canAddProposalContent: boolean;
  isLoadingProposals: boolean;
  canEdit: boolean;
  canPublish: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?(): void;
  entityName: string;
  entityId: number;
  isPublishing: boolean;
  isSaving: boolean;
  entitySubdomain: string;
  isTemplate: boolean;
  publication?: IPublication;
  userId: number;
  lastPath: string;
  messages: IStoryViewMessages;
  modalMessages: IModalMessages;
  editorMessages: IEditorMessages;
  story: IStory;
  storyContents: IStoryContent[];
  storyMeta: IStoryMeta;
  selectedBudget: IBudget | null;
  proposalFieldsDetails: IAllProposalDetails;
  onRepublishClick(): void;
  onShareVisibilityClick(): void;
  onClearContent(storyContent: IStoryContent): void;
  onResetContent(storyContent: IStoryContent): void;
  openModal(modal: string): IAction<string, string>;
  updateStoryContentDB(props: IUpdateStoryContentProps): Promise<void>;
  updateMetaStoryContent(storyContent: IStoryContent): Promise<void>;
  updateStoryContent(storyContent: IStoryContent | INewStoryContent): Promise<void>;
  updateStoryContents(storyContents: IStoryContent[]): Promise<void>;
  updateAllStoryContentsDB(props: {
    isTemplate: boolean;
    story: IStory;
    storyContents: IStoryContent[];
  }): Promise<void>;
};

export const Sidebar: React.FC<ISidebarProps> = (props) => {
  const {
    canAddProposalContent,
    canPublish,
    isCollapsed = false,
    onToggleCollapse,
    entityName,
    isPublishing,
    isSaving,
    entitySubdomain,
    isTemplate,
    lastPath,
    messages,
    story,
    storyContents,
    storyMeta,
    onRepublishClick,
    onShareVisibilityClick,
    onClearContent,
    onResetContent,
    openModal,
    updateStoryContentDB,
    updateMetaStoryContent,
    updateStoryContent,
    updateStoryContents,
    updateAllStoryContentsDB,
  } = props;

  const { description, createdAt, updatedAt, state, _id, public_url } = story;

  // Match legacy TabBar default: Proposal Content when proposals are enabled.
  const [activeTabIndex, setActiveTabIndex] = useState(canAddProposalContent ? 1 : 0);
  const [copyConfirmation, setCopyConfirmation] = useState('');

  const sortedStoryContents = orderStoryContents(sortStoryContents(storyContents));
  const sortedDateTimes = getNewestUpdatedAt(storyContents, updatedAt);
  const chipParams = getStoryStatusChipParams(story);

  const handleShare = () => {
    onShareVisibilityClick();
  };

  const handleRepublish = () => {
    onRepublishClick();
  };

  const renderShareButton = (): React.ReactElement | null => {
    if (isTemplate) return null;
    return canPublish ? (
      <Button
        className="story-share-button"
        variant="outlined"
        color="primary"
        size="small"
        onClick={handleShare}
        disabled={isSaving || isPublishing}
        data-test="story-share"
      >
        Share
      </Button>
    ) : null;
  };

  const renderRepublishButton = (): React.ReactElement | null => {
    const canRepublish = state === 'internalPublished' || state === 'published';
    if (isTemplate || !canRepublish) return null;
    return canPublish ? (
      <Button
        className="story-republish-button"
        variant="outlined"
        color="primary"
        size="small"
        onClick={handleRepublish}
        disabled={isSaving || isPublishing}
        data-test="story-republish"
      >
        {messages.republish}
      </Button>
    ) : null;
  };

  const renderStatusDescription = (storyState: IStoryState): React.ReactElement | null => {
    let descriptionText: string | null = null;
    switch (storyState) {
      case STATE_DRAFT:
        descriptionText = messages.statusDraftDescription;
        break;
      case STATE_PUBLISHED:
        descriptionText = messages.statusPublishedDescription;
        break;
      case STATE_INTERNAL_PUBLISHED:
        descriptionText = messages.statusInternalPublishedDescription;
        break;
      default:
        return null;
    }
    return (
      <Typography variant="caption" color="text.secondary">
        {descriptionText}
      </Typography>
    );
  };

  const renderPublishedStoriesInfo = (): React.ReactElement | null => {
    if (state !== STATE_PUBLISHED && state !== STATE_INTERNAL_PUBLISHED) return null;
    if (isTemplate) return null;

    let url: string;
    if (state === STATE_PUBLISHED) {
      const pattern = /^((http|https):\/\/)/;
      const publicUrl = public_url ?? '';
      url = !pattern.test(publicUrl) ? `http://${publicUrl}` : publicUrl;
    } else {
      url = `${OpenGovAppConfig.OPERATE_URL}/stories-internal/${_id}?entitySubdomain=${entitySubdomain}`;
    }

    return (
      <Box sx={styles.publishedInfoGroup}>
        <Box sx={styles.urlGroup}>
          <TextField
            id="story-url-field"
            label={messages.storyUrl}
            value={url}
            size="small"
            fullWidth
            inputProps={{ readOnly: true, 'data-test': 'story-url', className: 'no-format' }}
            data-test="story-url-label"
            sx={styles.urlField}
          />
          <Box sx={styles.urlButtons}>
            <CopyToClipboard
              text={url}
              onCopy={() => {
                setCopyConfirmation(messages.copy);
                setTimeout(() => setCopyConfirmation(''), 2000);
              }}
            >
              <Button variant="outlined" color="secondary" size="small" data-test="copy-story-url">
                {messages.copy}
              </Button>
            </CopyToClipboard>
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              color="secondary"
              size="small"
              data-test="view-story"
            >
              {messages.view}
            </Button>
          </Box>
          <Box aria-live="polite" aria-atomic="true" sx={styles.srOnly}>
            {copyConfirmation}
          </Box>
        </Box>
        {state === STATE_PUBLISHED && (
          <TextField
            id="embed-code-field"
            label={messages.embedCode}
            value={`<iframe src="${url}"></iframe>`}
            size="small"
            fullWidth
            multiline
            rows={2}
            inputProps={{
              id: 'embed-code-field',
              readOnly: true,
              'aria-label': messages.embedCode,
              'data-test': 'embed-code-value',
              className: 'no-format',
            }}
            InputLabelProps={{ htmlFor: 'embed-code-field' }}
            data-test="embed-code-label"
            sx={styles.urlField}
          />
        )}
      </Box>
    );
  };

  if (lastPath === PATH_EDIT) {
    return (
      <Box
        component="aside"
        aria-label="Story editor sidebar"
        sx={[styles.asideContainer, isCollapsed ? styles.asideContainerCollapsed : {}]}
      >
        {isCollapsed ? (
          <Box
            component="button"
            onClick={onToggleCollapse}
            aria-expanded={false}
            aria-label="Expand sidebar"
            sx={styles.collapsedButton}
          >
            <ChevronRightIcon fontSize="small" sx={styles.collapsedIcon} />
            <Box sx={styles.collapsedTitle}>
              {canAddProposalContent ? messages.pageLayoutAndProposals : messages.pageLayout}
            </Box>
          </Box>
        ) : (
          <Box sx={styles.sidebarContainer}>
            <Box sx={styles.storyContentsWrapper} data-test="stories-sidebar-container">
              <Box sx={styles.sidebarHeaderRow}>
                <Typography variant="h3" sx={styles.sidebarHeader}>
                  {canAddProposalContent ? messages.pageLayoutAndProposals : messages.pageLayout}
                </Typography>
                <IconButton
                  size="small"
                  onClick={onToggleCollapse}
                  aria-label="Collapse sidebar"
                  sx={styles.toggleButton}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Box>
              {canAddProposalContent ? (
                <Box>
                  <Tabs
                    value={activeTabIndex}
                    onChange={(_e, newValue: number) => setActiveTabIndex(newValue)}
                    sx={styles.tabs}
                  >
                    <Tab
                      label="Page Layout"
                      id="tab-page-layout"
                      aria-controls="tabpanel-page-layout"
                      sx={styles.tab}
                    />
                    <Tab
                      label="Proposal Content"
                      id="tab-proposal-content"
                      aria-controls="tabpanel-proposal-content"
                      data-test="proposal-content-path"
                      sx={styles.tab}
                    />
                  </Tabs>
                  <Collapse
                    in={activeTabIndex === 0}
                    role="tabpanel"
                    id="tabpanel-page-layout"
                    aria-labelledby="tab-page-layout"
                    sx={styles.tabPanel}
                  >
                    <StoryContents
                      entityName={entityName}
                      storyMeta={storyMeta}
                      storyContents={sortedStoryContents}
                      messages={messages}
                      story={story}
                      isTemplate={isTemplate}
                      updateStoryContentDB={updateStoryContentDB}
                      updateStoryContent={updateStoryContent}
                      updateStoryContents={updateStoryContents}
                      updateMetaStoryContent={updateMetaStoryContent}
                      updateAllStoryContentsDB={updateAllStoryContentsDB}
                      openModal={openModal}
                      onClearContent={onClearContent}
                      onResetContent={onResetContent}
                    />
                  </Collapse>
                  <Collapse
                    in={activeTabIndex === 1}
                    role="tabpanel"
                    id="tabpanel-proposal-content"
                    aria-labelledby="tab-proposal-content"
                    sx={styles.tabPanel}
                  >
                    <ProposalsView {...props} publicationId={props.publication?.id} />
                  </Collapse>
                </Box>
              ) : (
                <StoryContents
                  entityName={entityName}
                  storyMeta={storyMeta}
                  storyContents={sortedStoryContents}
                  messages={messages}
                  story={story}
                  isTemplate={isTemplate}
                  updateStoryContentDB={updateStoryContentDB}
                  updateStoryContent={updateStoryContent}
                  updateStoryContents={updateStoryContents}
                  updateMetaStoryContent={updateMetaStoryContent}
                  updateAllStoryContentsDB={updateAllStoryContentsDB}
                  openModal={openModal}
                  onClearContent={onClearContent}
                  onResetContent={onResetContent}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="aside"
      aria-label="Story overview sidebar"
      sx={[styles.root, isCollapsed ? styles.rootCollapsed : {}]}
    >
      {isCollapsed ? (
        <Box
          component="button"
          onClick={onToggleCollapse}
          aria-expanded={false}
          aria-label="Expand sidebar"
          sx={styles.collapsedButton}
        >
          <ChevronRightIcon fontSize="small" sx={styles.collapsedIcon} />
          <Box sx={styles.collapsedTitle}>Story Overview</Box>
        </Box>
      ) : (
        <Box sx={styles.info}>
          <Box sx={styles.sidebarHeaderRow}>
            <Typography variant="h3" sx={styles.sidebarHeader}>
              Story Overview
            </Typography>
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              aria-label="Collapse sidebar"
              sx={styles.toggleButton}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Box>
          {description && <Typography variant="body2">{description}</Typography>}
          <Box sx={styles.metaGroup}>
            <Box sx={styles.metaRow} data-test="story-created-at">
              <Typography sx={styles.metaLabel}>
                {messages.created}:{' '}
              </Typography>
              <Typography sx={styles.metaValue}>
                {moment.utc(createdAt).local().format('MMMM Do YYYY, h:mma')}
              </Typography>
            </Box>
            <Box sx={styles.metaRow} data-test="story-updated-at">
              <Typography sx={styles.metaLabel}>
                {messages.updated}:{' '}
              </Typography>
              <Typography sx={styles.metaValue}>
                {moment.utc(sortedDateTimes[0]).local().format('MMMM Do YYYY, h:mma')}
              </Typography>
            </Box>
          </Box>
          <Box sx={styles.statusGroup}>
            <Box sx={styles.badgeRow} data-test="story-visibility">
              <Typography sx={styles.metaLabel}>{messages.status}:</Typography>
              {chipParams && (
                <Chip
                  label={chipParams.text}
                  color={chipParams.color}
                  size="small"
                  className="status-badge"
                  sx={styles.chipMargin}
                />
              )}
            </Box>
            {renderStatusDescription(story.state)}
            <Box sx={styles.buttons}>
              {renderShareButton()}
              {renderRepublishButton()}
            </Box>
          </Box>
          {renderPublishedStoriesInfo()}
        </Box>
      )}
    </Box>
  );
};
